import { CreateFlagHasPotentialTripInput, ImportTypeEnum, StagedTrip, StagedTripIngestActionEnum } from '@/models/gen/graphql';
import { createNotification, getClasses } from '@/utils';

import EditableCell from '@/common/Table/cells/EditableCell';
import FlagsButton from './FlagsButton';
import Input from '@/components/Input';
import Logger from '@/utils/logs';
import NumberInput from '@/components/NumberInput';
import ScheduledCell from './ScheduledCell';
import Table from '@/common/Table';
import Toast from '@/models/Toast';
import createFlagHasPotentialTripBulk from '@/api/services/imports/createFlagHasPotentialTripBulk';
import { useEditFlagsModal } from '../EditFlagsModal';
import { useManifestPreviewContext } from './ManifestPreviewContext';
import { useManifestState } from '../store';

const log = Logger.of('ManifestPreviewTableRow');

const EDITABLE_FLAG_ACTIONS = [StagedTripIngestActionEnum.Create, StagedTripIngestActionEnum.Update, StagedTripIngestActionEnum.Restore];

export type ManifestPreviewTableRowProps = {
  row: StagedTrip;
  previewType: ImportTypeEnum;
  classes: string;
  isSelected: boolean;
  onEdit: (row: StagedTrip) => (field: string) => (val: string) => void;
  onSelect: (id: string | any[]) => void;
  isSelectable?: (row: StagedTrip) => boolean;
};

const ManifestPreviewTableRow = ({
  row,
  previewType,
  classes,
  isSelected,
  onEdit,
  onSelect,
  isSelectable,
}: ManifestPreviewTableRowProps): JSX.Element => {
  const {
    typeName,
    scheduled,
    bufferMinutes,
    airportCode,
    servicerIataAirlineCode,
    loopName,
    flightNumber,
    pilots,
    attendants,
    puLocation,
    doLocation,
    payerProvider,
    statusName,
    ingestAction,
  } = row;

  const flags = useManifestState(({ state }) => {
    const excludesMap = (state.excludedFlags[row.tripId] || []).reduce((acc, flagId) => ({ ...acc, [flagId]: true }), {});
    return (row.flags || []).filter((flagId) => !excludesMap[flagId]);
  });
  const { onRefreshPreview } = useManifestPreviewContext();
  // Use locally-tracked flags if available (reflects unsaved modal edits), else fall back to server data
  const canEditFlags = EDITABLE_FLAG_ACTIONS.includes(ingestAction) || !row.tripId;

  const openFlagsModal = () => {
    useEditFlagsModal.getState().setState((current) => ({
      ...current,
      show: true,
      value: Object.fromEntries(flags.map((id) => [id, true])),
      onSubmit: async (newValue: Record<string, boolean>) => {
        if (!row.potentialTripId) return log.error('openFlagsModal: potentialTripId is missing for row', row.id);

        const createPayload: CreateFlagHasPotentialTripInput[] = [];
        for (const [id, selected] of Object.entries(newValue)) {
          if (!selected) continue;
          if (!flags.includes(id)) createPayload.push({ flagId: id, potentialTripId: row.potentialTripId });
        }

        if (createPayload.length) {
          createFlagHasPotentialTripBulk(createPayload)
            .then(onRefreshPreview)
            .catch((err) => {
              log.error('openFlagsModal: createFlagHasPotentialTripBulk failed', err);
              createNotification('Preview is stale: auto-refreshing...', Toast.Type.WARNING, 'Import Preview');
              onRefreshPreview();
            });
        }
        // Use newValue as an O(1) lookup to find which flags were removed
        const removedFlagIds = flags.filter((id) => !newValue[id]);

        useManifestState.getState().setState((current) => {
          // Drop excluded entries that were re-added
          const excluded = (current.excludedFlags[row.tripId] || []).filter((excludedFlagId) => !newValue[excludedFlagId]);
          // Track newly removed flags so they can be excluded at ingest time
          for (const flagId of removedFlagIds) {
            if (!excluded.some((excludedFlagId) => excludedFlagId === flagId)) excluded.push(flagId);
          }

          return {
            ...current,
            excludedFlags: { ...current.excludedFlags, [row.tripId]: excluded },
          };
        });
      },
    }));
  };

  return (
    <Table.Row className={getClasses(classes)} id={row.tripId}>
      <Table.Cell className="SELECT">
        <Input
          type="checkbox"
          onChange={() => {
            const input = Array.isArray(row) ? row.filter(isSelectable).map((row) => row.id) : isSelectable(row) ? row?.id : undefined;
            if (!input) return;
            onSelect(input);
          }}
          checked={isSelected ? true : false}
          disabled={!isSelectable(row)}
        />
      </Table.Cell>
      {previewType === ImportTypeEnum.Addon && (
        <Table.Cell className="FLAGS d-print-none">
          <FlagsButton flags={flags} onClick={openFlagsModal} disabled={!canEditFlags} />
        </Table.Cell>
      )}
      <Table.Cell className="TYPE">{typeName}</Table.Cell>
      <ScheduledCell id={row.id} value={scheduled || ''} onChange={onEdit(row)('scheduled')} />
      {previewType !== ImportTypeEnum.Addon && <Table.Cell className="BUFF">{bufferMinutes}</Table.Cell>}
      <Table.Cell className="CITY">{airportCode}</Table.Cell>
      <Table.Cell className="AL">{servicerIataAirlineCode}</Table.Cell>
      <EditableCell
        className="FLT"
        value={loopName || flightNumber}
        onChange={(val: any): void => onEdit(row)(val.match(/\D/g) || val?.length > 4 ? 'loopName' : 'flightNumber')(val)}
      />
      <EditableCell className="PLT" as={NumberInput} value={pilots} onChange={onEdit(row)('pilots')} />
      <EditableCell className="FA" as={NumberInput} value={attendants} onChange={onEdit(row)('attendants')} />
      <Table.Cell className="PICKUP">{puLocation?.name}</Table.Cell>
      <Table.Cell className="DROPOFF">{doLocation?.name}</Table.Cell>
      <Table.Cell className="CLT">{payerProvider?.displayName}</Table.Cell>
      <Table.Cell className="STATUS">{statusName}</Table.Cell>
    </Table.Row>
  );
};

export default ManifestPreviewTableRow;
